/* ============================================================
   opulpet — slide-out cart + promotion progress
   Works with snippets/cart-drawer.liquid (config + i18n are
   injected as application/json script tags by that snippet).

   Responsibilities
     - intercept the product form submit and add via /cart/add.js
     - re-render drawer items, subtotal and promo bars from /cart.js
     - quantity +/- and remove through /cart/change.js
     - keep the header cart count in sync
   ============================================================ */
(function () {
  'use strict';

  var drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  var defaults = {
    moneyFormat: '${{amount}}',
    cartUrl: '/cart',
    checkoutUrl: '/checkout',
    freeShipping: false,
    fsMin: 0,
    tiers: []
  };

  var config = defaults;
  var i18n = {};

  try {
    var cfgEl = document.querySelector('[data-drawer-config]');
    if (cfgEl) config = Object.assign({}, defaults, JSON.parse(cfgEl.textContent));
  } catch (err) { /* keep defaults */ }

  try {
    var i18nEl = document.querySelector('[data-drawer-i18n]');
    if (i18nEl) i18n = JSON.parse(i18nEl.textContent) || {};
  } catch (err) { /* keep empty */ }

  /* ---------------- helpers ---------------- */

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function tpl(str, vars) {
    if (!str) return '';
    return String(str).replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : match;
    });
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;
    if (isNaN(number) || number == null) return 0;
    number = (number / 100.0).toFixed(precision);
    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return parts[1] ? dollars + decimal + parts[1] : dollars;
  }

  /* Mirrors Shopify's money_format placeholders so the drawer shows the same
     formatting as server-rendered Liquid prices. */
  function formatMoney(cents, fmt) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var formatString = fmt || config.moneyFormat;
    var match = formatString.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return String(cents);
    var value;
    switch (match[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' ', ','); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(match[0], value);
  }

  function money(cents) { return formatMoney(cents, config.moneyFormat); }

  function setLoading(on) { drawer.classList.toggle('is-loading', !!on); }

  /* ---------------- progress bars ---------------- */

  function sortedTiers() {
    return (config.tiers || [])
      .filter(function (t) { return t && t.min > 0; })
      .sort(function (a, b) { return a.min - b.min; });
  }

  function rowHtml(unlocked, message, pct) {
    return '<div class="promo__row' + (unlocked ? ' is-unlocked' : '') + '">' +
             '<p class="promo__msg">' + message + '</p>' +
             '<div class="promo__bar"><span class="promo__fill" style="width:' + pct + '%"></span></div>' +
           '</div>';
  }

  function renderApplied(cart) {
    var apps = (cart && cart.cart_level_discount_applications) || [];
    if (!apps.length) return '';
    return '<ul class="promo__applied">' + apps.map(function (d) {
      return '<li class="promo__applied-item">' +
               '<span class="promo__applied-name">' + esc(d.title) + '</span>' +
               '<span class="promo__applied-amount">−' + money(d.total_allocated_amount || 0) + '</span>' +
             '</li>';
    }).join('') + '</ul>';
  }

  function renderProgress(cart) {
    var host = drawer.querySelector('[data-drawer-progress]');
    if (!host) return;

    var total = cart.total_price;
    var html = renderApplied(cart);

    if (config.freeShipping && config.fsMin > 0) {
      if (total >= config.fsMin) {
        html += rowHtml(true, esc(i18n.free_shipping_unlocked), 100);
      } else {
        var fsPct = Math.min(100, Math.round((total / config.fsMin) * 100));
        html += rowHtml(
          false,
          esc(tpl(i18n.free_shipping_remaining, { amount: money(config.fsMin - total) })),
          fsPct
        );
      }
    }

    var tiers = sortedTiers();
    var active = null;
    var next = null;

    tiers.forEach(function (t) {
      if (total >= t.min && (!active || t.min > active.min)) active = t;
    });
    tiers.forEach(function (t) {
      if (total < t.min && !next) next = t;
    });

    if (next) {
      var pct = Math.min(100, Math.round((total / next.min) * 100));
      html += rowHtml(
        false,
        esc(tpl(i18n.tier_remaining, { amount: money(next.min - total), reward: money(next.off) })),
        pct
      );
    } else if (active) {
      html += rowHtml(true, esc(tpl(i18n.tier_unlocked, { reward: money(active.off) })), 100);
    }

    host.innerHTML = html ? '<div class="promo" data-promo>' + html + '</div>' : '';
  }

  /* ---------------- line items ---------------- */

  function itemImage(item) {
    if (!item.image) return '';
    var url = item.image + (item.image.indexOf('?') > -1 ? '&' : '?') + 'width=160';
    return '<img class="cart-drawer__item-img" src="' + esc(url) + '" alt="" loading="lazy">';
  }

  function renderItems(cart) {
    var host = drawer.querySelector('[data-drawer-items]');
    if (!host) return;

    if (!cart.item_count) {
      host.innerHTML = '<p class="cart-drawer__empty">' + esc(i18n.drawer_empty) + '</p>';
      return;
    }

    host.innerHTML = cart.items.map(function (item, index) {
      var title = item.product_title || item.title;
      var variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      var discountRow = '';
      var lineDiscount = (item.original_line_price || 0) - (item.final_line_price || 0);

      if (lineDiscount > 0) {
        discountRow = '<div class="cart-drawer__item-discount">' +
                        '<s>' + money(item.original_line_price) + '</s>' +
                        '<span class="promo__applied-amount">−' + money(lineDiscount) + '</span>' +
                      '</div>';
      }

      return '<div class="cart-drawer__item" data-key="' + esc(item.key) + '" data-line="' + (index + 1) + '">' +
               '<a href="' + esc(item.url) + '" class="cart-drawer__item-media">' + itemImage(item) + '</a>' +
               '<div class="cart-drawer__item-info">' +
                 '<a href="' + esc(item.url) + '" class="cart-drawer__item-name">' + esc(title) + '</a>' +
                 (variant ? '<div class="cart-drawer__item-variant">' + esc(variant) + '</div>' : '') +
                 '<div class="cart-drawer__item-qty">' +
                   '<button type="button" class="qty-btn" data-qty-minus aria-label="−">−</button>' +
                   '<input class="qty-input" type="number" min="0" value="' + esc(item.quantity) + '" data-qty-input aria-label="' + esc(i18n.qty) + '">' +
                   '<button type="button" class="qty-btn" data-qty-plus aria-label="+">+</button>' +
                 '</div>' +
                 discountRow +
               '</div>' +
               '<div class="cart-drawer__item-right">' +
                 '<div class="cart-drawer__item-price">' + money(item.final_line_price) + '</div>' +
                 '<button type="button" class="cart-drawer__item-remove" data-item-remove>' + esc(i18n.remove) + '</button>' +
               '</div>' +
             '</div>';
    }).join('');
  }

  /* ---------------- state ---------------- */

  function updateCount(count) {
    var nodes = document.querySelectorAll('[data-cart-count]');
    for (var i = 0; i < nodes.length; i++) nodes[i].textContent = count;
  }

  function refresh(cart) {
    renderProgress(cart);
    renderItems(cart);

    var sub = drawer.querySelector('[data-drawer-subtotal]');
    if (sub) sub.textContent = money(cart.total_price);

    updateCount(cart.item_count);
    drawer.classList.toggle('is-empty', cart.item_count === 0);
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.json(); });
  }

  function changeQty(line, quantity) {
    setLoading(true);
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) { refresh(cart); })
      .catch(function () { window.location.href = config.cartUrl; })
      .then(function () { setLoading(false); });
  }

  /* ---------------- open / close ---------------- */

  function openDrawer() {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-lock');
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-lock');
  }

  drawer.addEventListener('click', function (event) {
    if (event.target.closest('[data-cart-drawer-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    var row = event.target.closest('.cart-drawer__item');
    if (!row) return;
    var line = parseInt(row.dataset.line, 10);

    if (event.target.closest('[data-item-remove]')) {
      changeQty(line, 0);
      return;
    }

    var input = row.querySelector('[data-qty-input]');
    var qty = input ? parseInt(input.value, 10) || 0 : 0;

    if (event.target.closest('[data-qty-minus]')) {
      changeQty(line, Math.max(0, qty - 1));
    } else if (event.target.closest('[data-qty-plus]')) {
      changeQty(line, qty + 1);
    }
  });

  drawer.addEventListener('change', function (event) {
    var input = event.target.closest('[data-qty-input]');
    if (!input) return;
    var row = input.closest('.cart-drawer__item');
    if (!row) return;
    changeQty(parseInt(row.dataset.line, 10), Math.max(0, parseInt(input.value, 10) || 0));
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeDrawer();
  });

  /* Header cart icon opens the drawer instead of navigating to /cart */
  var cartLinks = document.querySelectorAll('.cart-link');
  for (var c = 0; c < cartLinks.length; c++) {
    cartLinks[c].addEventListener('click', function (event) {
      event.preventDefault();
      setLoading(true);
      fetchCart()
        .then(function (cart) { refresh(cart); openDrawer(); })
        .catch(function () { window.location.href = config.cartUrl; })
        .then(function () { setLoading(false); });
    });
  }

  /* ---------------- add to cart (Ajax) ---------------- */

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || typeof form.action !== 'string') return;
    if (form.action.indexOf('/cart/add') === -1) return;

    /* Capture phase + stopImmediatePropagation keeps Shopify's own
       standard-actions.js from also handling this submit. */
    event.preventDefault();
    event.stopImmediatePropagation();

    var button = form.querySelector('[type="submit"]');
    var originalText = button ? button.textContent : '';
    if (button) {
      button.disabled = true;
      if (i18n.adding) button.textContent = i18n.adding;
    }

    var added = false;

    fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })
      .then(function (res) {
        if (!res.ok) throw new Error('add failed');
        added = true;
        return res.json();
      })
      .then(fetchCart)
      .then(function (cart) { refresh(cart); })
      .catch(function () {
        if (!added && form.submit) form.submit(); /* native fallback */
      })
      .then(function () {
        if (added) openDrawer();
        setLoading(false);
        if (button) {
          button.disabled = false;
          button.textContent = originalText;
        }
      });
  }, true);
})();
